import {
  CaretDown,
  CaretDownIcon,
  ShieldChevronIcon,
} from '@phosphor-icons/react';
import { format, isSameDay, parseISO } from 'date-fns';
import { CircleXIcon, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface FilterDatePickerProps {
  label: string;
  value: string; // ISO string or empty
  onChange: (value: string) => void;
}

export const FilterDatePicker = ({
  label,
  value,
  onChange,
}: FilterDatePickerProps) => {
  const [open, setOpen] = React.useState(false);
  const date = value ? parseISO(value) : undefined;

  const handleSelect = (selected: Date | undefined) => {
    if (!selected) return;
    // Toggle off if same date clicked again
    if (date && isSameDay(selected, date)) {
      onChange('');
      setOpen(false);
    } else {
      onChange(format(new Date(selected), 'yyyy-MM-dd'));
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'w-full min-h-[38px] flex items-center justify-between rounded-lg border border-gray-700 bg-black text-white hover:bg-zinc-900',
            !value && 'text-zinc-400'
          )}
        >
          <span className="flex items-center space-x-1">
            <span className="text-zinc-500">{label}:</span>
            {value ? (
              <span>{format(parseISO(value), 'dd/MM/yyyy')}</span>
            ) : (
              <span className="text-zinc-50 data-[placeholder]:text-zinc-50">
                All
              </span>
            )}
          </span>
          <div className="flex items-center space-x-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="p-0 m-0 bg-transparent border-none cursor-pointer"
              >
                <CircleXIcon style={{ width: '16px', height: '16px' }} />
              </button>
            )}
          </div>
          <button>
            <CaretDownIcon style={{ width: '14px', height: '14px' }} />
          </button>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-black border border-zinc-800">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
          className="bg-black text-white"
        />
      </PopoverContent>
    </Popover>
  );
};
